import { setFulfilled, setPending, setRejected } from '@ms/yammer-data-support';
import { Reducer } from 'redux';

import {
  FeatureAccessSettingsClientLoadFulfilledAction,
  FeatureAccessSettingsClientLoadPendingAction,
  FeatureAccessSettingsClientLoadRejectedAction,
  FeatureAccessSettingsUpdateForAudienceAction,
} from '../../domains/featureAccessSettings/actions.types';
import {
  EditNetworkQuestionSettingRejectedPayload,
  EditNetworkQuestionSettingsFulfilledAction,
  EditNetworkQuestionSettingsPendingAction,
  EditNetworkQuestionSettingsRejectedAction,
} from '../../domains/featureAccessSettings/editNetworkQuestionSettingsActions.types';

import { FeatureAccessSettingsLifecycle } from './types';

export type FeatureAccessSettingsAction =
  | FeatureAccessSettingsClientLoadPendingAction
  | FeatureAccessSettingsClientLoadRejectedAction
  | FeatureAccessSettingsClientLoadFulfilledAction
  | EditNetworkQuestionSettingsFulfilledAction
  | FeatureAccessSettingsUpdateForAudienceAction
  | EditNetworkQuestionSettingsPendingAction
  | EditNetworkQuestionSettingsRejectedAction;

const initialState: FeatureAccessSettingsLifecycle = {
  pending: false,
  fulfilled: undefined,
  rejected: undefined,
};

const updateNetworkQuestionsSettings = (
  state: FeatureAccessSettingsLifecycle,
  {
    networkQuestionsMode,
    isGroupQuestionsAIImporterEnabled,
    isNetworkQuestionsAIFeaturesEnabled,
    isNetworkQuestionsAIImporterEnabled,
  }: EditNetworkQuestionSettingsFulfilledAction | EditNetworkQuestionSettingsPendingAction
) => {
  const isAIFeaturesEnabled =
    isNetworkQuestionsAIFeaturesEnabled === undefined ? {} : { isNetworkQuestionsAIFeaturesEnabled };
  const isAIImporterGroupEnabled =
    isGroupQuestionsAIImporterEnabled === undefined ? {} : { isGroupQuestionsAIImporterEnabled };
  const isAIImporterAnswersEnabled =
    isNetworkQuestionsAIImporterEnabled === undefined ? {} : { isNetworkQuestionsAIImporterEnabled };

  return state.fulfilled
    ? {
        ...state,
        fulfilled: {
          ...state.fulfilled,
          networkQuestionsEnablementMode: networkQuestionsMode,
          ...isAIImporterGroupEnabled,
          ...isAIFeaturesEnabled,
          ...isAIImporterAnswersEnabled,
        },
      }
    : state;
};

const updateFeatureAccessSettingsForAudience = (
  state: FeatureAccessSettingsLifecycle,
  payload: FeatureAccessSettingsUpdateForAudienceAction['payload']
) => {
  if (!state.fulfilled) {
    return state;
  }

  return {
    ...state,
    fulfilled: { ...state.fulfilled, ...payload },
  };
};

const updateNetworkQuestionsEnablementModeWithPreviousValue = (
  state: FeatureAccessSettingsLifecycle,
  payload: EditNetworkQuestionSettingRejectedPayload
) => {
  if (!state.fulfilled) {
    return state;
  }

  return {
    ...state,
    fulfilled: {
      ...state.fulfilled,
      ...{ networkQuestionsEnablementMode: payload.prevSettingValue },
    },
  };
};

const reducer: Reducer<FeatureAccessSettingsLifecycle, FeatureAccessSettingsAction> = (
  state = initialState,
  action
) => {
  switch (action.type) {
    case 'FEATURE_ACCESS_SETTINGS.LOAD_PENDING':
      return setPending();
    case 'FEATURE_ACCESS_SETTINGS.LOAD_REJECTED':
      return setRejected(action.payload.error);
    case 'FEATURE_ACCESS_SETTINGS.LOAD_FULFILLED':
      return setFulfilled(action.payload);
    case 'NETWORK_QUESTION_SETTINGS.EDIT_FULFILLED':
      return updateNetworkQuestionsSettings(state, action);
    case 'NETWORK_QUESTION_SETTINGS.EDIT_PENDING':
      return updateNetworkQuestionsSettings(state, action);
    case 'NETWORK_QUESTION_SETTINGS.EDIT_REJECTED':
      return updateNetworkQuestionsEnablementModeWithPreviousValue(state, action.payload);
    case 'FEATURE_ACCESS_SETTINGS.UPDATE_HAS_NETWORK_WIDE_AUDIENCE':
      return updateFeatureAccessSettingsForAudience(state, action.payload);
    default:
      return state;
  }
};

export default reducer;
